import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { usePersistedState } from "@/hooks/usePersistedState";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Eye, Filter, Download, Send, Search, LayoutList, Rows3, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  "Contante", "Assegno", "Bancomat", "Bonifico", "Bollettino postale",
  "Sospeso contante", "Sospeso assegno", "Sospeso bonifico", "Sospeso POS", "Sospeso postale",
  "Recupero contante", "Recupero assegno", "Recupero bonifico", "Recupero POS", "Recupero postale",
];
const PAGE_SIZE = 50;

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function StatusBadge({ stato }) {
  const map = {
    aperto: { label: "Aperto", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    chiuso_auto: { label: "Chiuso auto", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    chiuso_manuale: { label: "Chiuso manuale", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    riaperto: { label: "Riaperto", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const it = map[stato] || { label: stato, cls: "bg-slate-100" };
  return <Badge variant="outline" className={`${it.cls} rounded-full px-2.5 py-0.5 text-xs`}>{it.label}</Badge>;
}

const EMPTY_FILTERS = {
  user_id: "",
  data_from: "",
  data_to: "",
  stato: "",
  only_urgent: false,
  solo_non_versati: false,
  metodo_pagamento: "",
  importo_min: "",
  importo_max: "",
};

export default function Storico() {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = usePersistedState("storico:view", "sheet"); // "sheet" | "row"
  const [q, setQ] = usePersistedState("storico:q", "");
  const [page, setPage] = usePersistedState("storico:page", 1);
  const [filters, setFilters] = usePersistedState("storico:filters", EMPTY_FILTERS);
  const filtersActive = !!q || JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setQ(""); setPage(1); };

  useEffect(() => {
    // Includiamo anche agente e utenze interne — così l'agente titolare filtra
    // i propri fogli e il consultazione può isolare qualunque utente dell'agenzia.
    api.get("/users").then((r) => setUsers(
      (r.data || []).filter((u) => ["collaboratore", "agente", "interno"].includes(u.role) && u.stato !== "disattivato")
    ));
    api.get("/billing/plan").then((r) => setRetention(r.data.retention_label || null)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      params[k] = v;
    });
    // Solo in modalità riga per riga chiediamo anche le entries (payload più corposo).
    // Nota: in vista "row" NON passiamo `user_id` al backend, perché il filtro
    // "Collaboratore" agisce sul collaboratore imputato (`_collab_display`), non
    // sull'owner del foglio cassa. Il filtro viene applicato client-side.
    if (viewMode === "row") {
      params.include_entries = true;
      delete params.user_id;
    }
    try {
      const r = await api.get("/cash-sheets", { params });
      setRows(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filters, viewMode]);

  useEffect(() => { load(); }, [load]);
  // Reset paginazione al cambio filtri/ricerca/vista
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setPage(1);
  }, [q, viewMode, filters, setPage]);

  // Costruisce la lista PIATTA di righe (entries) per la vista "riga per riga":
  // filtrata per contraente/numero polizza + tutti i filtri della card (metodo,
  // importo, urgenti, non versati, collaboratore imputato). Il filtro data è
  // già applicato lato backend sui fogli.
  const flatEntries = useMemo(() => {
    if (viewMode !== "row") return [];
    const needle = q.trim().toLowerCase();
    const importoMin = filters.importo_min !== "" ? Number(filters.importo_min) : null;
    const importoMax = filters.importo_max !== "" ? Number(filters.importo_max) : null;
    // Nome+cognome del collaboratore selezionato per il filtro (case-insensitive).
    const collabTarget = (() => {
      if (!filters.user_id) return "";
      const u = users.find((x) => x.id === filters.user_id);
      if (!u) return "";
      return `${u.nome || ""} ${u.cognome || ""}`.trim().toLowerCase();
    })();
    const out = [];
    for (const r of rows) {
      const sheetDate = r.sheet?.data_foglio_cassa;
      for (const e of (r.entries || [])) {
        if (collabTarget && (e._collab_display || "").trim().toLowerCase() !== collabTarget) continue;
        if (filters.metodo_pagamento && e.metodo_pagamento !== filters.metodo_pagamento) continue;
        if (filters.only_urgent && !e.urgente) continue;
        if (filters.solo_non_versati && e.versato) continue;
        const imp = Number(e.importo || 0);
        if (importoMin != null && !Number.isNaN(importoMin) && imp < importoMin) continue;
        if (importoMax != null && !Number.isNaN(importoMax) && imp > importoMax) continue;
        if (needle) {
          const hay = `${e.contraente || ""} ${e.numero_polizza || ""}`.toLowerCase();
          if (!hay.includes(needle)) continue;
        }
        out.push({ ...e, _sheet_date: sheetDate, _sheet_id: r.sheet.id });
      }
    }
    // Ordina per data foglio cassa desc, poi per data_ora_inserimento_reale desc.
    out.sort((a, b) => {
      if (a._sheet_date !== b._sheet_date) return (b._sheet_date || "").localeCompare(a._sheet_date || "");
      return (b.data_ora_inserimento_reale || "").localeCompare(a.data_ora_inserimento_reale || "");
    });
    return out;
  }, [rows, viewMode, q, filters, users]);

  const totalPages = Math.max(1, Math.ceil(flatEntries.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageEntries = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return flatEntries.slice(start, start + PAGE_SIZE);
  }, [flatEntries, currentPage]);

  // Applica anche il filtro q in modalità "sheet" (client-side): un foglio matcha
  // se il collaboratore contiene la stringa. Le entries per contraente/polizza sono
  // filtrate solo in vista "row" (dove abbiamo le righe complete).
  const sheetRows = useMemo(() => {
    if (viewMode !== "sheet") return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const owner = `${r.user?.nome || ""} ${r.user?.cognome || ""}`.toLowerCase();
      return owner.includes(needle);
    });
  }, [rows, viewMode, q]);

  const download = async (kind) => {
    // Costruisce gli stessi query params della vista riga per riga.
    const params = { fmt: kind === "excel" ? "excel" : "pdf" };
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      if (k === "user_id") return; // gestito come collab_name
      params[k] = v;
    });
    if (q.trim()) params.q = q.trim();
    if (filters.user_id) {
      const u = users.find((x) => x.id === filters.user_id);
      if (u) params.collab_name = `${u.nome || ""} ${u.cognome || ""}`.trim();
    }
    try {
      const r = await api.get("/cash-sheets/rows/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = r.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `storico_righe.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadFile = async (sheetId, cognome, dataSheet, kind) => {
    try {
      const res = await api.get(`/cash-sheets/${sheetId}/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foglio_cassa_${cognome || ""}_${dataSheet}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resend = async (sheetId) => {
    try {
      await api.post(`/cash-sheets/${sheetId}/resend`);
      toast.success("Riepilogo email reinviato");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Archivio</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Storico fogli cassa</h1>
        <p className="text-sm text-slate-500 mt-1">
          Consulta, filtra e scarica i fogli cassa archiviati.
          {retention && (
            <span className="ml-1">Storico conservato per <strong>{retention}</strong> in base al piano (oltre, eliminazione automatica).</span>
          )}
        </p>
      </div>

      {/* Toolbar: ricerca + toggle vista */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={viewMode === "row" ? "Cerca per contraente o n. polizza…" : "Cerca collaboratore…"}
              className="pl-9"
              data-testid="storico-search"
            />
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 self-start md:self-auto" role="group" aria-label="Modalità vista">
            <button
              type="button"
              onClick={() => setViewMode("sheet")}
              data-testid="storico-view-sheet"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "sheet" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <LayoutList className="w-4 h-4" /> Per foglio
            </button>
            <button
              type="button"
              onClick={() => setViewMode("row")}
              data-testid="storico-view-row"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "row" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Rows3 className="w-4 h-4" /> Riga per riga
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-slate-700" />
          <h2 className="font-heading font-semibold">Filtri</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Da</Label>
            <Input type="date" value={filters.data_from} onChange={(e) => setFilters({ ...filters, data_from: e.target.value })} data-testid="filter-data-from" />
          </div>
          <div>
            <Label>A</Label>
            <Input type="date" value={filters.data_to} onChange={(e) => setFilters({ ...filters, data_to: e.target.value })} data-testid="filter-data-to" />
          </div>
          <div>
            <Label>Collaboratore</Label>
            <Select value={filters.user_id || "all"} onValueChange={(v) => setFilters({ ...filters, user_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-user"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stato</Label>
            <Select value={filters.stato || "all"} onValueChange={(v) => setFilters({ ...filters, stato: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-stato"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="aperto">Aperto</SelectItem>
                <SelectItem value="chiuso_auto">Chiuso automaticamente</SelectItem>
                <SelectItem value="chiuso_manuale">Chiuso manualmente</SelectItem>
                <SelectItem value="riaperto">Riaperto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Metodo pagamento</Label>
            <Select value={filters.metodo_pagamento || "all"} onValueChange={(v) => setFilters({ ...filters, metodo_pagamento: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-metodo"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Importo min €</Label>
            <Input type="number" value={filters.importo_min} onChange={(e) => setFilters({ ...filters, importo_min: e.target.value })} data-testid="filter-importo-min" />
          </div>
          <div>
            <Label>Importo max €</Label>
            <Input type="number" value={filters.importo_max} onChange={(e) => setFilters({ ...filters, importo_max: e.target.value })} data-testid="filter-importo-max" />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={filters.only_urgent} onCheckedChange={(v) => setFilters({ ...filters, only_urgent: !!v })} data-testid="filter-urgent" />
              Solo con righe urgenti
            </label>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={filters.solo_non_versati} onCheckedChange={(v) => setFilters({ ...filters, solo_non_versati: !!v })} data-testid="filter-non-versati" />
              Solo clienti non versati
            </label>
          </div>
          {filtersActive && (
            <div className="flex items-end">
              <Button size="sm" variant="outline" onClick={resetFilters} data-testid="storico-reset-filters">
                Azzera filtri
              </Button>
            </div>
          )}
        </div>
      </Card>

      {viewMode === "sheet" ? (
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Data</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Collaboratore</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Stato</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Righe</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Urgenti</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Totale</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Da versare</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Caricamento…</td></tr>}
              {!loading && sheetRows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500" data-testid="empty-storico">Nessun foglio cassa.</td></tr>}
              {sheetRows.map((r, idx) => (
                <tr key={r.sheet.id} className="hover:bg-slate-50" data-testid={`storico-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{formatDateIT(r.sheet.data_foglio_cassa)}</td>
                  <td className="px-4 py-3 text-slate-700">{r.user?.nome} {r.user?.cognome}</td>
                  <td className="px-4 py-3"><StatusBadge stato={r.sheet.stato} /></td>
                  <td className="px-4 py-3 text-right font-mono-num text-slate-700">{r.totals.count_totale}</td>
                  <td className="px-4 py-3 text-right font-mono-num">
                    {r.totals.count_urgenti > 0 ? <span className="text-red-600 font-semibold">{r.totals.count_urgenti}</span> : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-900">{formatEUR(r.totals.totale_generale)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">
                    {(r.totals.totale_non_versato || 0) > 0
                      ? (
                        <div className="flex flex-col items-end">
                          <span className="text-amber-700 font-semibold">{formatEUR(r.totals.totale_non_versato)}</span>
                          {r.totals.versamento_mode === "polizze" && (r.totals.titoli_non_versati || 0) > 0 && (
                            <span className="text-[10px] text-amber-700/80">
                              {r.totals.titoli_non_versati} titol{r.totals.titoli_non_versati === 1 ? "o" : "i"}
                            </span>
                          )}
                        </div>
                      )
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/admin/foglio-cassa/${r.sheet.id}`}>
                        <Button size="sm" variant="outline" data-testid={`view-${idx}`}><Eye className="w-3.5 h-3.5" /></Button>
                      </Link>
                      <Button size="sm" variant="outline" onClick={() => downloadFile(r.sheet.id, r.user?.cognome, r.sheet.data_foglio_cassa, "pdf")} data-testid={`pdf-${idx}`}><Download className="w-3.5 h-3.5" /> PDF</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadFile(r.sheet.id, r.user?.cognome, r.sheet.data_foglio_cassa, "excel")} data-testid={`xls-${idx}`}><Download className="w-3.5 h-3.5" /> XLS</Button>
                      <Button size="sm" variant="outline" onClick={() => resend(r.sheet.id)} data-testid={`resend-${idx}`}><Send className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      ) : (
      <Card className="border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 text-sm gap-3 flex-wrap">
          <span className="text-slate-600" data-testid="storico-row-count">
            {loading ? "Caricamento…" : `${flatEntries.length} righe`}
          </span>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => download("pdf")} disabled={loading || flatEntries.length === 0} data-testid="storico-row-export-pdf">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("excel")} disabled={loading || flatEntries.length === 0} data-testid="storico-row-export-xls">
              <Download className="w-4 h-4 mr-1" /> Excel
            </Button>
            {totalPages > 1 && (
              <>
                <Button size="sm" variant="outline" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="storico-page-prev">
                  <ChevronLeft className="w-4 h-4" /> Precedente
                </Button>
                <span className="text-slate-700 font-medium" data-testid="storico-page-info">
                  Pagina {currentPage} di {totalPages}
                </span>
                <Button size="sm" variant="outline" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="storico-page-next">
                  Successiva <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Ora</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Inserito da</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Collaboratore</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Contraente</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">N. polizza</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Metodo</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Importo</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-8 text-slate-500">Caricamento…</td></tr>}
              {!loading && pageEntries.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="empty-storico-row">Nessuna riga trovata.</td></tr>
              )}
              {pageEntries.map((e, idx) => {
                const prev = pageEntries[idx - 1];
                const isNewDay = !prev || prev._sheet_date !== e._sheet_date;
                const ora = (e.data_ora_inserimento_reale || "").slice(11, 16);
                return [
                  isNewDay && (
                    <tr key={`day-${e._sheet_date}-${idx}`} className="bg-slate-100">
                      <td colSpan={7} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600" data-testid={`day-sep-${e._sheet_date}`}>
                        {formatDateIT(e._sheet_date)}
                      </td>
                    </tr>
                  ),
                  <tr key={e.id} className={`border-b border-slate-100 hover:bg-slate-50 ${e.urgente ? "bg-red-50/40" : ""}`} data-testid={`storico-entry-${idx}`}>
                    <td className="px-4 py-2 font-mono-num text-slate-600">{ora || "—"}</td>
                    <td className="px-4 py-2 text-slate-700">{e._creator_display || "—"}</td>
                    <td className="px-4 py-2 text-slate-700">{e._collab_display || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2 text-slate-900">
                      {e.urgente && <span className="mr-1 text-red-600" title="Urgente">●</span>}
                      {e.contraente || "—"}
                    </td>
                    <td className="px-4 py-2 font-mono-num text-slate-600">{e.numero_polizza || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{e.metodo_pagamento || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono-num font-semibold text-slate-900">{formatEUR(e.importo)}</td>
                  </tr>,
                ];
              })}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
}
